import classNames from "classnames";
import React, {ComponentProps, PropsWithChildren} from "react";
import {HTMLAttributes, useContext} from "react";
import {
  BiCloudDownload,
  BiSearchAlt,
  BiSortDown,
  BiSortUp,
} from "react-icons/bi";
import {twMerge} from "tailwind-merge";
import {ColumnConfig, TableState} from "./useTable";
import {Loader} from "../../../../../components/src/lib/Loader";
import {TableColumnFilter} from "./TableColumnFilter";

type ColumnContextType = {
  key: string;
  config: ColumnConfig;
};
export const TableContext = React.createContext<TableState>({} as TableState);
export const ColumnContext = React.createContext<ColumnContextType>(
  {} as ColumnContextType,
);

export const TableProvider = ({
  children,
  state,
}: PropsWithChildren<{state: TableState}>) => {
  return (
    <TableContext.Provider value={state}>{children}</TableContext.Provider>
  );
};

export const TableContainer = ({
  children,
  className,
  ...otherProps
}: ComponentProps<"div">) => {
  const state = useContext(TableContext);

  return (
    <div
      className={twMerge(
        "border border-base-300 rounded-lg overflow-x-scroll lg:overflow-x-auto relative",
        className,
      )}
      {...otherProps}
    >
      {state.loading && (
        <p className="absolute inset-0 z-20 flex justify-center items-center bg-base-200/50">
          <Loader />
        </p>
      )}
      {children}
    </div>
  );
};

export const Th = ({
  children,
  className,
  k,
  ...otherProps
}: HTMLAttributes<HTMLTableCellElement> & {k: string}) => {
  const state = useContext(TableContext);

  const config = state.columns[k]!;

  return (
    <ColumnContext.Provider value={{key: k, config}}>
      <th className={twMerge(className, "group")} {...otherProps}>
        <div className="space-y-1 flex flex-col">
          <div className="flex items-center gap-x-2">
            {children}
            {config?.sort && <SortIcon onClick={() => state.toggleOrder(k)} />}
          </div>
          {config?.filter && <TableColumnFilter />}
        </div>
      </th>
    </ColumnContext.Provider>
  );
};

export const Td = ({
  children,
  className,
  idx,
  ...otherProps
}: HTMLAttributes<HTMLTableCellElement> & {idx: number}) => {
  const state = useContext(TableContext);

  return (
    <td
      className={twMerge(
        classNames({"border-b-0": state.expandedRows[idx]}),
        className,
      )}
      {...otherProps}
    >
      {children}
    </td>
  );
};

export const SortIcon = ({onClick}: ComponentProps<"svg">) => {
  const state = useContext(TableContext);
  const columnConfig = useContext(ColumnContext);

  const sortActive = state.sortBy === columnConfig.key && state.sortOrder;

  const className = classNames("w-4 h-4 cursor-pointer", {
    "invisible group-hover:visible text-gray-300": !sortActive,
  });

  return state.sortOrder === "desc" ? (
    <BiSortUp className={className} onClick={onClick} />
  ) : (
    <BiSortDown className={className} onClick={onClick} />
  );
};

export const RowsPerPage = () => {
  const state = useContext(TableContext);

  return (
    <select
      className="select select-bordered select-sm mr-2"
      onChange={(e) => state.changeRowsPerPage(Number(e.target.value))}
      value={state.pageSize}
    >
      <option disabled>Rows</option>
      {state.getPagesInfo().allowedRowsPerPage.map(({n, label}) => (
        <option key={n} value={n}>
          {label}
        </option>
      ))}
    </select>
  );
};

export const Pagination = ({disbursement}: {disbursement?: boolean}) => {
  const state = useContext(TableContext);

  const pagesInfo = state.getPagesInfo();
  return (
    <div
      className={classNames(
        "space-x-4 flex flex-col space-y-2 md:space-y-0 md:flex-row items-center",
        {
          "pointer-events-none": disbursement && state.nSelected > 0,
        },
      )}
    >
      <span className="text-sm">
        Showing &nbsp;
        {pagesInfo.fromRowNum}-{pagesInfo.toRowNum} of{" "}
        {state.totalRowCount || state.originalData.length}
      </span>
      <RowsPerPage />
      <div className="btn-group">
        {pagesInfo.layout.map((btn, i) => (
          <button
            key={i}
            className={classNames("btn btn-sm", {
              "btn-primary": btn.type === "active",
              "btn-disabled":
                btn.type === "disabled" ||
                (disbursement && state.nSelected > 0),
            })}
            onClick={btn.action ? () => state.paginate(btn.action!) : undefined}
          >
            {btn.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export const EmptyTableRow = ({children}: PropsWithChildren) => {
  const state = useContext(TableContext);

  return !state.getVisibleData().length ? (
    <tr>
      <td colSpan={999}>
        <div className="flex justify-center">{children}</div>
      </td>
    </tr>
  ) : null;
};

export const TableFooter = ({
  children,
  className,
  ...otherProps
}: PropsWithChildren & ComponentProps<"div">) => {
  return (
    <div
      className={classNames(
        twMerge(
          "py-6 flex flex-col space-y-2 md:space-y-0 md:flex-row justify-end items-center",
          className,
        ),
      )}
      {...otherProps}
    >
      {children}
    </div>
  );
};

export const TableHeader = ({
  children,
  className,
  ...otherProps
}: PropsWithChildren & ComponentProps<"div">) => {
  return (
    <div
      className={classNames(
        twMerge(
          "py-6 flex flex-col lg:flex-row lg:justify-end lg:items-center",
          className,
        ),
      )}
      {...otherProps}
    >
      {children}
    </div>
  );
};

export const Expanded = ({children, idx}: PropsWithChildren<{idx: number}>) => {
  const state = useContext(TableContext);

  return state.expandedRows[idx] ? (
    <tr>
      <td colSpan={999}>{children}</td>
    </tr>
  ) : null;
};

export const Search = ({disabled}: {disabled?: boolean}) => {
  const state = useContext(TableContext);

  return (
    <div className="form-control">
      <div className="input-group">
        <input
          type="text"
          placeholder="Search here"
          className="input input-sm input-bordered"
          value={state.search}
          onChange={(e) => state.setSearch(e.target.value)}
          disabled={disabled}
        />
        <button
          className={classNames("btn btn-sm btn-square", {
            "btn-disabled": disabled,
          })}
        >
          <BiSearchAlt />
        </button>
      </div>
    </div>
  );
};

export const SelectAllCheckBox = () => {
  const table = useContext(TableContext);

  return (
    <input
      type="checkbox"
      checked={table.allVisibleSelected}
      className="checkbox checkbox-xs flex justify-center items-center"
      onChange={() =>
        table.allVisibleSelected
          ? table.clearSelection()
          : table.selectAllVisible()
      }
    />
  );
};

export const SelectCheckBox = ({idx}: {idx: number}) => {
  const table = useContext(TableContext);

  return (
    <input
      type="checkbox"
      checked={table.isSelected(idx)}
      className="checkbox checkbox-xs flex justify-center items-center"
      onChange={() => table.toggleSelection(idx)}
      disabled={table.shouldDisableSelect(table.selection, idx)}
    />
  );
};

export const ExportToCsv = ({
  fileName,
  className,
  innerText,
  ...otherProps
}: ComponentProps<"button"> & {fileName: string; innerText?: string}) => {
  const table = useContext(TableContext);

  return (
    <button
      className={twMerge(
        classNames("btn btn-sm", {"loading btn-disabled": table.loading}),
        className,
      )}
      onClick={() => table.exportToCsv(fileName)}
      {...otherProps}
    >
      <BiCloudDownload className="mr-1" /> {innerText ? innerText : "CSV"}
    </button>
  );
};
